import React, { useEffect, useState } from "react";

import dayjs from "dayjs";

import { ModernLandlordModal } from "js/components/Landlords/ModernLandlordModal";
import { LandlordDetails } from "js/components/Tenants/TenantReportDetails/typing";
import { Button } from "js/components/ui/button";
import {
  DescriptionList,
  DescriptionItem,
} from "js/components/ui/description-list";
import { useUpdateLandlord } from "../../../../hooks/useLandlords";
import { toast } from "../../../../utils/toast";
import { toTitleCase } from "../../../../utils/utils";

interface LandlordDetailsComponentProps {
  landlord: LandlordDetails;
  onUpdate: (updatedLandlord: LandlordDetails) => void;
  folio?: string;
  isModalOpen?: boolean;
  onModalOpenChange?: (open: boolean) => void;
}

const LandlordDetailsComponent: React.FC<LandlordDetailsComponentProps> = ({
  landlord,
  onUpdate,
  folio,
  isModalOpen,
  onModalOpenChange,
}) => {
  const [isLegalPerson, setIsLegalPerson] = useState<boolean>(false);
  const [internalModalOpen, setInternalModalOpen] = useState(false);
  const hasExternalModalControl =
    isModalOpen !== undefined && onModalOpenChange !== undefined;
  const isModalVisible = hasExternalModalControl
    ? isModalOpen
    : internalModalOpen;
  const setIsModalVisible = hasExternalModalControl
    ? onModalOpenChange
    : setInternalModalOpen;

  const updateLandlordMutation = useUpdateLandlord({
    onSuccess: (data, variables) => {
      toast.success("Datos actualizados");
      const updatedLandlord = {
        ...landlord,
        ...variables.landlord,
      };
      onUpdate(updatedLandlord);
      setIsModalVisible(false);
    },
    onError: () => {
      toast.error("Algo salió mal, inténtelo de nuevo.");
    },
  });

  useEffect(() => {
    setIsLegalPerson(landlord.legal_type == 2);
  }, [landlord]);

  const handleSave = (landlordData: any) => {
    const queryParams = folio ? { folio } : undefined;

    const payload = {
      ...landlord,
      ...landlordData,
    };

    return updateLandlordMutation.mutateAsync({
      landlord: payload,
      landlordId: landlord.id!,
      queryParams,
    });
  };

  const fullName = toTitleCase(
    `${landlord.name} ${landlord.first_surname ?? ""} ${landlord.second_surname ?? ""}`.trim(),
  );

  return (
    <>
      {!landlord.verified && !hasExternalModalControl && (
        <div className="mb-4 flex justify-end">
          <Button variant="ghost" size="sm" onClick={() => setIsModalVisible(true)}>
            Editar
          </Button>
        </div>
      )}

      <DescriptionList>
        <DescriptionItem
          label={`Nombre${isLegalPerson ? " del representante legal" : ""}`}
          copyable
        >
          {fullName}
        </DescriptionItem>
        <DescriptionItem label="Correo" copyable>
          {landlord.email}
        </DescriptionItem>
        <DescriptionItem label="Teléfono" copyable>
          {landlord.phone_number
            ? `${landlord?.dial_code ?? ""} ${landlord.phone_number}`
            : null}
        </DescriptionItem>
        <DescriptionItem label="RFC" copyable>
          {landlord.rfc ?? null}
        </DescriptionItem>
        <DescriptionItem label="Fecha de Nacimiento">
          {landlord?.birth_date
            ? dayjs(landlord.birth_date).format("DD/MM/YYYY")
            : null}
        </DescriptionItem>
        <DescriptionItem label="CURP" copyable>
          {landlord.curp ?? null}
        </DescriptionItem>
      </DescriptionList>

      <ModernLandlordModal
        isOpen={isModalVisible}
        onOpenChange={setIsModalVisible}
        onSave={handleSave}
        selectedLandlord={landlord}
      />
    </>
  );
};

export default LandlordDetailsComponent;
